import { useState, useEffect, useMemo } from 'react';
import { Clock, CheckCircle2, XCircle, Calendar, Search, ChevronDown, ChevronRight, AlertCircle, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import AdminLayout from '@/components/AdminLayout';
import { adminAPI } from '@/lib/api';
import { formatDate, formatTime } from '@/lib/dateUtils';
import { useBranchFilter } from '@/components/BranchFilterContext';

const STATUS_STYLES = {
  approved: 'bg-[#026536]/10 text-[#026536]',
  rejected: 'bg-red-50 text-red-500',
  pending: 'bg-[#dea439]/10 text-[#dea439]',
};

export default function AdminLateSanctions() {
  const [sanctions, setSanctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const { branch } = useBranchFilter();

  useEffect(() => { fetchSanctions(); }, [branch]);

  const fetchSanctions = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getLateSanctions(null, 1, 500, branch);
      setSanctions(res.data.records || res.data || []);
    } catch {
      toast.error('Failed to load late sanctions');
    } finally {
      setLoading(false);
    }
  };

  const handleStatus = async (id, status) => {
    try {
      await adminAPI.updateLateSanctionStatus(id, status);
      toast.success(`Late sanction ${status}`);
      fetchSanctions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  // Filter + search
  const filtered = useMemo(() => {
    return sanctions.filter(s => {
      if (statusFilter !== 'all' && s.status !== statusFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        return s.user_name?.toLowerCase().includes(q) || s.user_phone?.includes(q);
      }
      return true;
    });
  }, [sanctions, statusFilter, search]);

  // Group by date
  const todayStr = new Date().toISOString().split('T')[0];
  const todayPending = filtered.filter(s => s.attendance_date === todayStr && s.status === 'pending');
  const todayResolved = filtered.filter(s => s.attendance_date === todayStr && s.status !== 'pending');
  const past = filtered.filter(s => s.attendance_date !== todayStr);

  // Group past by date
  const pastByDate = useMemo(() => {
    const groups = {};
    past.forEach(s => {
      const d = s.attendance_date || 'Unknown';
      if (!groups[d]) groups[d] = [];
      groups[d].push(s);
    });
    return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0]));
  }, [past]);

  // Stats
  const stats = useMemo(() => ({
    total: sanctions.length,
    pending: sanctions.filter(s => s.status === 'pending').length,
    approvedToday: sanctions.filter(s => s.status === 'approved' && s.attendance_date === todayStr).length,
    rejectedToday: sanctions.filter(s => s.status === 'rejected' && s.attendance_date === todayStr).length,
  }), [sanctions, todayStr]);

  return (
    <AdminLayout>
      <div className="space-y-5 animate-fade-in">
        {/* Header */}
        <div>
          <h1 data-testid="admin-late-sanctions-title" className="text-xl font-extrabold text-[#1a2529] tracking-tight">Late Sanctions</h1>
          <p className="text-sm text-[#1a2529]/50 mt-0.5">Review late arrival sanction requests</p>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatPill label="Total" value={stats.total} color="text-[#1a2529]" bg="bg-white border border-border/30" />
          <StatPill label="Pending" value={stats.pending} color="text-[#dea439]" bg="bg-[#dea439]/10" />
          <StatPill label="Approved Today" value={stats.approvedToday} color="text-[#026536]" bg="bg-[#026536]/10" />
          <StatPill label="Rejected Today" value={stats.rejectedToday} color="text-red-500" bg="bg-red-50" />
        </div>

        {/* Filters */}
        <div className="flex gap-3 items-center flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#1a2529]/30" size={15} />
            <Input data-testid="sanction-search" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search employee..." className="pl-9 h-9 bg-white text-sm" />
          </div>
          <div className="flex gap-1 bg-white rounded-lg p-0.5 border border-border/30">
            {['all', 'pending', 'approved', 'rejected'].map(s => (
              <button key={s} data-testid={`filter-${s}`} onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 rounded-md text-xs font-bold transition-colors capitalize ${statusFilter === s ? (s === 'pending' ? 'bg-[#dea439] text-white' : s === 'approved' ? 'bg-[#026536] text-white' : s === 'rejected' ? 'bg-red-500 text-white' : 'bg-[#1a2529] text-white') : 'text-[#1a2529]/50 hover:text-[#1a2529]'}`}>
                {s}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><div className="h-8 w-8 border-2 border-[#026536] border-t-transparent rounded-full animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="space-y-5">
            {/* TODAY - Pending (Prominent) */}
            {todayPending.length > 0 && (
              <div data-testid="today-pending-section">
                <div className="flex items-center gap-2 mb-3">
                  <div className="h-2 w-2 rounded-full bg-[#dea439] animate-pulse" />
                  <h2 className="text-sm font-extrabold text-[#1a2529] uppercase tracking-wide">Today - Needs Action ({todayPending.length})</h2>
                </div>
                <div className="space-y-2">
                  {todayPending.map(s => (
                    <SanctionCard key={s.id} item={s} onAction={handleStatus} prominent />
                  ))}
                </div>
              </div>
            )}

            {/* TODAY - Resolved */}
            {todayResolved.length > 0 && (
              <div data-testid="today-resolved-section">
                <h2 className="text-xs font-bold text-[#1a2529]/40 uppercase tracking-wide mb-2">Today - Resolved ({todayResolved.length})</h2>
                <div className="space-y-1.5">
                  {todayResolved.map(s => (
                    <SanctionRow key={s.id} item={s} />
                  ))}
                </div>
              </div>
            )}

            {/* PAST - Grouped by Date */}
            {pastByDate.length > 0 && (
              <div data-testid="past-section">
                <h2 className="text-xs font-bold text-[#1a2529]/40 uppercase tracking-wide mb-3">Past Sanctions</h2>
                <div className="space-y-2">
                  {pastByDate.map(([date, items]) => (
                    <DateGroup key={date} date={date} items={items} onAction={handleStatus} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function StatPill({ label, value, color, bg }) {
  return (
    <div className={`${bg} rounded-xl px-4 py-3 shadow-sm`}>
      <span className={`text-xl font-extrabold ${color}`}>{value}</span>
      <p className={`text-[9px] font-bold uppercase ${color} opacity-50 mt-0.5`}>{label}</p>
    </div>
  );
}

function SanctionCard({ item: s, onAction, prominent }) {
  return (
    <div data-testid={`sanction-card-${s.id}`}
      className={`rounded-xl p-4 border shadow-sm animate-slide-up ${prominent ? 'bg-[#dea439]/5 border-[#dea439]/20' : 'bg-white border-border/30'}`}>
      <div className="flex items-center justify-between mb-2.5">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-[#dea439]/10 flex items-center justify-center text-sm font-bold text-[#dea439] shrink-0">
            {s.user_name?.[0]?.toUpperCase()}
          </div>
          <div>
            <p className="font-bold text-sm text-[#1a2529]">{s.user_name}</p>
            <p className="text-[11px] text-[#1a2529]/40">{s.user_phone}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-xs font-semibold text-[#1a2529]/60">{formatDate(s.attendance_date)}</p>
          {s.check_in_time && <p className="text-[10px] text-[#1a2529]/30">In: {formatTime(s.check_in_time)}</p>}
        </div>
      </div>

      {s.reason && <p className="text-xs text-[#1a2529]/50 mb-3 pl-[52px] italic">"{s.reason}"</p>}

      {s.status === 'pending' && (
        <div className="flex gap-2 pl-[52px]">
          <Button data-testid={`approve-${s.id}`} onClick={() => onAction(s.id, 'approved')} size="sm"
            className="flex-1 h-8 bg-[#026536] hover:bg-[#026536]/90 text-white font-semibold text-xs gap-1">
            <CheckCircle2 size={13} /> Approve
          </Button>
          <Button data-testid={`reject-${s.id}`} onClick={() => onAction(s.id, 'rejected')} size="sm" variant="outline"
            className="flex-1 h-8 border-red-200 text-red-500 hover:bg-red-50 font-semibold text-xs gap-1">
            <XCircle size={13} /> Reject
          </Button>
        </div>
      )}
    </div>
  );
}

function SanctionRow({ item: s }) {
  return (
    <div data-testid={`sanction-row-${s.id}`} className="bg-white rounded-lg px-4 py-2.5 border border-border/20 flex items-center justify-between gap-3">
      <div className="flex items-center gap-3 min-w-0">
        <div className="h-8 w-8 rounded-full bg-[#f4f4f5] flex items-center justify-center text-[10px] font-bold text-[#1a2529]/30 shrink-0">
          {s.user_name?.[0]?.toUpperCase()}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-[#1a2529] truncate">{s.user_name}</p>
          {s.reason && <p className="text-[10px] text-[#1a2529]/30 truncate">{s.reason}</p>}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {s.approved_by_role && (
          <span className="text-[9px] font-bold text-[#1a2529]/20 uppercase">{s.approved_by_role === 'rm' ? `RM: ${s.approved_by}` : 'Admin'}</span>
        )}
        <Badge className={`${STATUS_STYLES[s.status]} border-0 text-[10px] font-bold capitalize`}>{s.status}</Badge>
      </div>
    </div>
  );
}

function DateGroup({ date, items, onAction }) {
  const [open, setOpen] = useState(false);
  const pendingCount = items.filter(i => i.status === 'pending').length;
  const hasPending = pendingCount > 0;

  return (
    <div className={`rounded-xl border ${hasPending ? 'border-[#dea439]/20 bg-[#dea439]/[0.02]' : 'border-border/20 bg-white'} overflow-hidden`}>
      <button data-testid={`date-group-${date}`} onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-[#f4f4f5]/50 transition-colors">
        <div className="flex items-center gap-3">
          {open ? <ChevronDown size={14} className="text-[#1a2529]/30" /> : <ChevronRight size={14} className="text-[#1a2529]/30" />}
          <Calendar size={13} className="text-[#1a2529]/30" />
          <span className="text-xs font-bold text-[#1a2529]">{formatDate(date)}</span>
          <span className="text-[10px] text-[#1a2529]/30">{items.length} request{items.length > 1 ? 's' : ''}</span>
        </div>
        <div className="flex items-center gap-1.5">
          {pendingCount > 0 && <Badge className="bg-[#dea439]/10 text-[#dea439] border-0 text-[9px] font-bold">{pendingCount} pending</Badge>}
          {items.filter(i => i.status === 'approved').length > 0 && <Badge className="bg-[#026536]/10 text-[#026536] border-0 text-[9px]">{items.filter(i => i.status === 'approved').length} approved</Badge>}
          {items.filter(i => i.status === 'rejected').length > 0 && <Badge className="bg-red-50 text-red-500 border-0 text-[9px]">{items.filter(i => i.status === 'rejected').length} rejected</Badge>}
        </div>
      </button>
      {open && (
        <div className="border-t border-border/10 divide-y divide-border/10">
          {items.map(s => (
            s.status === 'pending' ? (
              <div key={s.id} className="px-4 py-3">
                <SanctionCard item={s} onAction={onAction} />
              </div>
            ) : (
              <SanctionRow key={s.id} item={s} />
            )
          ))}
        </div>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-border/30">
      <CheckCircle2 className="mx-auto text-[#026536]/15 mb-3" size={40} />
      <p className="font-semibold text-[#1a2529]/40">No sanctions found</p>
      <p className="text-xs text-[#1a2529]/25 mt-1">All clear for now</p>
    </div>
  );
}
